"use client";
import {
  type AccountFetcherClient,
  type Authority,
  getEmberStateAddress,
  type PhoenixBuilderAddresses,
  type PhoenixInstructionClient,
} from "@ellipsis-labs/rise";
import { type Address, createSolanaRpc } from "@solana/kit";

import { getRpcUrl, USDC_MINT_ADDRESS } from "@/lib/config";
import { resolvePhoenixAddressesForEnv } from "@/lib/phoenix/traderAddresses";

type Rpc = ReturnType<typeof createSolanaRpc>;

let rpc: Rpc | null = null;
let fetcher: AccountFetcherClient | null = null;
let instructionClient: PhoenixInstructionClient | null = null;

const getRpc = (): Rpc => {
  if (rpc === null) {
    const url = getRpcUrl();
    if (!url) {
      throw new Error(
        "NEXT_PUBLIC_RPC_URL is not set — cannot read on-chain accounts"
      );
    }
    rpc = createSolanaRpc(url);
  }
  return rpc;
};

const base64ToBytes = (b64: string): Uint8Array => {
  if (typeof atob === "function") {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }
  // Node fallback (SSR / tests).
  if (typeof Buffer === "undefined") {
    throw new Error("No base64 decoder available");
  }
  return Buffer.from(b64, "base64");
};

/** Lazily-constructed AccountFetcherClient backed by the Solana RPC. */
export const getAccountFetcherClient = (): AccountFetcherClient => {
  if (fetcher !== null) return fetcher;

  const client = getRpc();
  fetcher = {
    fetchAccount: async (address: Address) => {
      const response = await client
        .getAccountInfo(address, { encoding: "base64" })
        .send();
      if (response.value === null) {
        throw new Error(`Account not found: ${String(address)}`);
      }
      const [b64] = response.value.data;
      return { data: base64ToBytes(b64) };
    },
  };
  return fetcher;
};

/**
 * Lazily-constructed PhoenixInstructionClient. This is a superset of
 * AccountFetcherClient that also carries the program / log / global
 * configuration / USDC mint / ember state addresses that rise's instruction
 * builders and flows (like `buildWithdrawFlow`) need. Addresses derive from
 * the current PHOENIX_ENV.
 */
export const getPhoenixInstructionClient =
  async (): Promise<PhoenixInstructionClient> => {
    if (instructionClient !== null) return instructionClient;

    const base = getAccountFetcherClient();
    const resolved = resolvePhoenixAddressesForEnv();
    const emberStateAddress = await getEmberStateAddress(
      resolved.programAddress
    );

    const addresses: PhoenixBuilderAddresses = {
      phoenixProgramAddress: resolved.programAddress,
      logAuthorityAddress: resolved.logAuthorityAddress,
      globalConfigurationAddress: resolved.globalConfigurationAddress,
      usdcMintAddress: USDC_MINT_ADDRESS,
      emberStateAddress,
    };

    instructionClient = {
      fetchAccount: base.fetchAccount,
      addresses,
    };
    return instructionClient;
  };

/** Returns a value as a rise-branded Authority (base58 Solana pubkey). */
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const asAuthority = (value: string): Authority => value as Authority;
